import React, {
  useLayoutEffect,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import { clearBodyLocks } from 'tua-body-scroll-lock';
import classnames from 'classnames';
import { off, on } from '@ifit/native-webview-interface';
import {
  notifyTrackingEvent,
  setToolbarTitle,
  hideModality,
  showModality,
} from '../../../store/to-native';
import { isSmallSearchDevice } from './is-small-search';
import { searchText as searchPageLabelText } from '../../../i18n/translations';
import SearchResults from './search-results';
import './index.scss';
import { QueryPerformanceTracker } from '../analytics/query-performance';
import { getSearchState } from '../state/search-state';
import { parseQueryString, parseURL } from '../state/query-string';
import { hideFilterOptions } from '../../../helpers/filter-options';
import { getSortOptions } from '../../../helpers/sorting';
import { useEntitiesInfiniteQuery } from '../../../client/query/entities';
import { withTheme } from '@ifit/themes/withTheme';
import { useSelector } from 'react-redux';
import { IRootReducer } from '../../../reducers';
import { useConfig } from '../../../store/reducers/config';
import { applyChallengeToIncludedEntities } from '../../../helpers/apply-challenge-to-included-entities';
import { useFeatureFlags } from '../../../helpers/good-cop-helper';

const Search = (): JSX.Element => {
  const [appliedFilters, setAppliedFilters] = useState(null);
  const [currentFilters, setCurrentFilters] = useState(null);
  const [appliedForApi, setAppliedForApi] = useState(null);
  const { entryPoint, deviceType } = useConfig();
  const challengeStoreFF = 'featureflag://flag?key=challenge-store&type=bool';
  const featureFlags = useFeatureFlags([challengeStoreFF]);
  const doEnableChallengeStore = featureFlags[challengeStoreFF]
    ? (featureFlags[challengeStoreFF] as boolean)
    : false;

  const queryPerformanceTracker = useMemo(
    () => new QueryPerformanceTracker(),
    [],
  );

  const { dataMemo, query, fetchNextPageCallback } = useEntitiesInfiniteQuery({
    ...appliedForApi,
  });

  const { displayError } = useSelector((state: IRootReducer) => state.error);

  useLayoutEffect(() => {
    setToolbarTitle(searchPageLabelText());
    on('callNative', queryPerformanceTracker.analyzeQueryPerformance);
  }, [queryPerformanceTracker]);

  const sendQueryPerformanceData = useCallback(
    (page = 1) => {
      const applied = applyChallengeToIncludedEntities(
        getSearchState().appliedForAPI,
        doEnableChallengeStore,
      );
      if (page === 1) {
        window.scrollTo(0, 0);
      }
      if (applied.query) {
        queryPerformanceTracker.queried(applied, page);
      }
    },
    [queryPerformanceTracker, doEnableChallengeStore],
  );

  const searchStateChanged = useCallback(
    (params, appliedChanged) => {
      if (appliedChanged) {
        const appliedFiltersForApi = applyChallengeToIncludedEntities(
          getSearchState().appliedForAPI,
          doEnableChallengeStore,
        );
        setAppliedFilters(params.appliedFilters);
        setCurrentFilters(params.currentFilters);
        setAppliedForApi(appliedFiltersForApi);
        sendQueryPerformanceData();
        sendAppliedFiltersTrackingEvent();
      }
    },
    [sendQueryPerformanceData, doEnableChallengeStore],
  );

  useEffect(() => {
    getSearchState().onStateChanged(searchStateChanged);
    const previous = getSearchState().previous;
    if (previous) {
      getSearchState().applyPreviousFilters();
      const applied = applyChallengeToIncludedEntities(
        getSearchState().appliedForAPI,
        doEnableChallengeStore,
      );
      setAppliedForApi(applied);
    }
    const enteringOnSearch = entryPoint?.startsWith('/search');
    const queryString = {
      ...parseQueryString(),
      ...(enteringOnSearch ? parseURL(entryPoint) : {}),
    };
    if (enteringOnSearch) {
      getSearchState().changeFromQueryString(queryString);
    }
    if (!queryString.showFilterOptions) {
      getSearchState().applyCurrentFilters();
      sendAppliedFiltersTrackingEvent();
    }
    hideModality();

    return () => {
      getSearchState().offStateChanged(searchStateChanged);
      off('callNative', queryPerformanceTracker.analyzeQueryPerformance);
      clearBodyLocks();
      hideFilterOptions();
      showModality();
    };
  }, [
    entryPoint,
    searchStateChanged,
    queryPerformanceTracker,
    doEnableChallengeStore,
  ]);

  function sendAppliedFiltersTrackingEvent(): void {
    notifyTrackingEvent('AppliedFilters', getSearchState().appliedWithLength);
  }

  function getMoreResults(): void {
    fetchNextPageCallback();
    sendQueryPerformanceData(dataMemo.nextPage);
  }

  const selectedFiltersCount = getSearchState().appliedCount;
  const usingFilters = getSearchState().usingFilters;

  if (displayError && !dataMemo.data) {
    return null;
  }

  return (
    <div
      className={classnames('search-results', {
        'small-search-experience': isSmallSearchDevice(deviceType),
      })}
    >
      <SearchResults
        searchText={appliedFilters?.query}
        isLoading={query.isLoading}
        results={dataMemo}
        sortOptions={getSortOptions(Boolean(currentFilters?.query))}
        selectedSortingOption={getSearchState().sortIndex}
        loadMore={getMoreResults}
        usingFilters={usingFilters}
        filterCount={selectedFiltersCount}
        hasMoreItems={dataMemo.hasMoreItems}
        totalItems={dataMemo.totalItems}
      />
    </div>
  );
};

export default withTheme(Search);
